<script setup>
import { ref, computed, nextTick, reactive, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { required, helpers, url } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useToggle } from '@vueuse/core';

import EditSheet from 'dashboard/components-next/polaris/cards/EditSheet.vue';
import FoldingGroup from 'dashboard/components-next/polaris/cards/FoldingGroup.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import AccessToken from 'dashboard/routes/dashboard/settings/profile/AccessToken.vue';

const props = defineProps({
  type: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
  selectedBot: {
    type: Object,
    default: () => ({}),
  },
});

const MODAL_TYPES = {
  CREATE: 'create',
  EDIT: 'edit',
};

const store = useStore();
const { t } = useI18n();
// Phase 9.3 (R2-9-5): the bot form lives in the kit's EditSheet. `dialogRef` keeps the old open/close contract
// for the list page; the sheet adds the "Discard changes?" guard.
const sheetRef = ref(null);
const isOpen = ref(false);
const snapshot = ref('');
const uiFlags = useMapGetter('agentBots/getUIFlags');

const formState = reactive({
  botName: '',
  botDescription: '',
  botUrl: '',
  botAvatar: null,
  botAvatarUrl: '',
});

const [showAccessToken, toggleAccessToken] = useToggle();
const accessToken = ref('');
const botSecret = ref('');

const dialogRef = {
  open: () => {
    isOpen.value = true;
    nextTick(() => {
      snapshot.value = JSON.stringify(formState);
    });
  },
  close: () => {
    isOpen.value = false;
  },
};

const v$ = useVuelidate(
  {
    botName: {
      required: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.NAME'),
        required
      ),
    },
    botUrl: {
      required: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.URL'),
        required
      ),
      url: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.VALID_URL'),
        url
      ),
    },
  },
  formState
);

const isLoading = computed(() =>
  props.type === MODAL_TYPES.CREATE
    ? uiFlags.value.isCreating
    : uiFlags.value.isUpdating
);

const dialogTitle = computed(() => {
  if (showAccessToken.value) {
    return t('AGENT_BOTS.ACCESS_TOKEN.TITLE');
  }

  return props.type === MODAL_TYPES.CREATE
    ? t('AGENT_BOTS.ADD.TITLE')
    : t('AGENT_BOTS.EDIT.TITLE');
});

const dialogDescription = computed(() => {
  if (showAccessToken.value) {
    return t('AGENT_BOTS.ACCESS_TOKEN.DESCRIPTION');
  }
  return '';
});

const confirmButtonLabel = computed(() =>
  props.type === MODAL_TYPES.CREATE
    ? t('AGENT_BOTS.FORM.CREATE')
    : t('AGENT_BOTS.FORM.UPDATE')
);

const botNameError = computed(() =>
  v$.value.botName.$error ? v$.value.botName.$errors[0]?.$message : ''
);

const botUrlError = computed(() =>
  v$.value.botUrl.$error ? v$.value.botUrl.$errors[0]?.$message : ''
);

const showAccessTokenInput = computed(
  () =>
    showAccessToken.value ||
    props.type === MODAL_TYPES.EDIT ||
    accessToken.value
);

const resetForm = () => {
  Object.assign(formState, {
    botName: '',
    botDescription: '',
    botUrl: '',
    botAvatar: null,
    botAvatarUrl: '',
  });
  v$.value.$reset();
};

const handleImageUpload = ({ file, url: avatarUrl }) => {
  formState.botAvatar = file;
  formState.botAvatarUrl = avatarUrl;
};

const handleAvatarDelete = async () => {
  if (props.selectedBot?.id) {
    try {
      await store.dispatch(
        'agentBots/deleteAgentBotAvatar',
        props.selectedBot.id
      );
      formState.botAvatar = null;
      formState.botAvatarUrl = '';
      useAlert(t('AGENT_BOTS.AVATAR.SUCCESS_DELETE'));
    } catch (error) {
      useAlert(t('AGENT_BOTS.AVATAR.ERROR_DELETE'));
    }
  } else {
    formState.botAvatar = null;
    formState.botAvatarUrl = '';
  }
};

const handleSubmit = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;
  if (showAccessToken.value) return;

  const botData = {
    name: formState.botName,
    description: formState.botDescription,
    outgoing_url: formState.botUrl,
    bot_type: 'webhook',
    avatar: formState.botAvatar,
  };

  const isCreate = props.type === MODAL_TYPES.CREATE;

  try {
    const actionPayload = isCreate
      ? botData
      : { id: props.selectedBot.id, data: botData };

    const response = await store.dispatch(
      `agentBots/${isCreate ? 'create' : 'update'}`,
      actionPayload
    );

    const alertKey = isCreate
      ? t('AGENT_BOTS.ADD.API.SUCCESS_MESSAGE')
      : t('AGENT_BOTS.EDIT.API.SUCCESS_MESSAGE');
    useAlert(alertKey);

    // Show access token and secret after creation
    if (isCreate) {
      const {
        access_token: responseAccessToken,
        secret: responseSecret,
        id,
      } = response || {};

      if (id && responseAccessToken) {
        accessToken.value = responseAccessToken;
        botSecret.value = responseSecret || '';
        toggleAccessToken(true);
      } else {
        accessToken.value = '';
        botSecret.value = '';
        dialogRef.close();
      }
    } else {
      dialogRef.close();
    }

    resetForm();
  } catch (error) {
    const errorKey = isCreate
      ? t('AGENT_BOTS.ADD.API.ERROR_MESSAGE')
      : t('AGENT_BOTS.EDIT.API.ERROR_MESSAGE');
    useAlert(errorKey);
  }
};

const initializeForm = () => {
  if (props.selectedBot && Object.keys(props.selectedBot).length) {
    const {
      name,
      description,
      outgoing_url: botUrl,
      thumbnail,
      bot_config: botConfig,
      access_token: botAccessToken,
      secret: botSecretValue,
    } = props.selectedBot;
    formState.botName = name || '';
    formState.botDescription = description || '';
    formState.botUrl = botUrl || botConfig?.webhook_url || '';
    formState.botAvatarUrl = thumbnail || '';

    if (props.type === MODAL_TYPES.EDIT) {
      if (botAccessToken) accessToken.value = botAccessToken;
      if (botSecretValue) botSecret.value = botSecretValue;
    }
  } else {
    resetForm();
  }
};

const onCopyToken = async value => {
  await copyTextToClipboard(value);
  useAlert(t('AGENT_BOTS.ACCESS_TOKEN.COPY_SUCCESSFUL'));
};

const onCopySecret = async value => {
  await copyTextToClipboard(value || botSecret.value);
  useAlert(t('AGENT_BOTS.SECRET.COPY_SUCCESS'));
};

const onResetSecret = async () => {
  const response = await store.dispatch(
    'agentBots/resetSecret',
    props.selectedBot.id
  );
  if (response) {
    botSecret.value = response.secret;
    useAlert(t('AGENT_BOTS.SECRET.RESET_SUCCESS'));
  } else {
    useAlert(t('AGENT_BOTS.SECRET.RESET_ERROR'));
  }
};

const onResetToken = async () => {
  const response = await store.dispatch(
    'agentBots/resetAccessToken',
    props.selectedBot.id
  );
  if (response) {
    accessToken.value = response.access_token;
    useAlert(t('AGENT_BOTS.ACCESS_TOKEN.RESET_SUCCESS'));
  } else {
    useAlert(t('AGENT_BOTS.ACCESS_TOKEN.RESET_ERROR'));
  }
};

const scope = computed(
  () => `${t('SIDEBAR.SETTINGS')} · ${t('AGENT_BOTS.HEADER')}`
);
const dirty = computed(
  () =>
    isOpen.value &&
    !showAccessToken.value &&
    JSON.stringify(formState) !== snapshot.value
);
const saveLabel = computed(() =>
  showAccessToken.value ? t('SETTINGS_PIECES.DONE') : confirmButtonLabel.value
);

const closeModal = () => {
  if (!showAccessToken.value) v$.value?.$reset();
  accessToken.value = '';
  botSecret.value = '';
  toggleAccessToken(false);
};

watch(isOpen, value => {
  if (!value) closeModal();
});

const onSave = () => {
  if (showAccessToken.value) {
    dialogRef.close();
    return;
  }
  handleSubmit();
};

const runGuarded = action =>
  sheetRef.value ? sheetRef.value.runGuarded(action) : action();

watch(() => props.selectedBot, initializeForm, { immediate: true, deep: true });

defineExpose({ dialogRef, runGuarded });
</script>

<template>
  <EditSheet
    ref="sheetRef"
    v-model:open="isOpen"
    :title="dialogTitle"
    :scope="scope"
    :dirty="dirty"
    :save-label="saveLabel"
    :can-save="!isLoading"
    @save="onSave"
  >
    <form class="grid gap-4" @submit.prevent="onSave">
      <p
        v-if="dialogDescription"
        class="type-polaris-body text-polaris-muted-foreground"
      >
        {{ dialogDescription }}
      </p>
      <FoldingGroup
        v-if="!showAccessToken || type === MODAL_TYPES.EDIT"
        :title="$t('SETTINGS_PIECES.SECTIONS.DETAILS')"
      >
        <div class="grid gap-4">
          <div class="flex flex-col items-start gap-2">
            <span class="type-polaris-label">
              {{ $t('AGENT_BOTS.FORM.AVATAR.LABEL') }}
            </span>
            <Avatar
              :src="formState.botAvatarUrl"
              :name="formState.botName"
              :size="68"
              allow-upload
              icon-name="i-lucide-bot-message-square"
              @upload="handleImageUpload"
              @delete="handleAvatarDelete"
            />
          </div>
          <Input
            id="bot-name"
            v-model="formState.botName"
            :label="$t('AGENT_BOTS.FORM.NAME.LABEL')"
            :placeholder="$t('AGENT_BOTS.FORM.NAME.PLACEHOLDER')"
            :message="botNameError"
            :message-type="botNameError ? 'error' : 'info'"
            @blur="v$.botName.$touch()"
          />
          <TextArea
            id="bot-description"
            v-model="formState.botDescription"
            :label="$t('AGENT_BOTS.FORM.DESCRIPTION.LABEL')"
            :placeholder="$t('AGENT_BOTS.FORM.DESCRIPTION.PLACEHOLDER')"
          />
          <Input
            id="bot-url"
            v-model="formState.botUrl"
            :label="$t('AGENT_BOTS.FORM.WEBHOOK_URL.LABEL')"
            :placeholder="$t('AGENT_BOTS.FORM.WEBHOOK_URL.PLACEHOLDER')"
            :message="botUrlError"
            :message-type="botUrlError ? 'error' : 'info'"
            @blur="v$.botUrl.$touch()"
          />
        </div>
      </FoldingGroup>

      <FoldingGroup
        v-if="showAccessTokenInput || botSecret"
        :title="$t('AGENT_BOTS.ACCESS_TOKEN.TITLE')"
      >
        <div class="grid gap-4">
          <div v-if="botSecret && type === MODAL_TYPES.EDIT" class="grid gap-1">
            <span class="type-polaris-label">
              {{ $t('AGENT_BOTS.SECRET.LABEL') }}
            </span>
            <AccessToken
              :value="botSecret"
              @on-copy="onCopySecret"
              @on-reset="onResetSecret"
            />
          </div>
          <div v-if="showAccessTokenInput" class="grid gap-1">
            <span v-if="type === MODAL_TYPES.EDIT" class="type-polaris-label">
              {{ $t('AGENT_BOTS.ACCESS_TOKEN.TITLE') }}
            </span>
            <AccessToken
              v-if="type === MODAL_TYPES.EDIT"
              :value="accessToken"
              @on-copy="onCopyToken"
              @on-reset="onResetToken"
            />
            <AccessToken
              v-else
              :value="accessToken"
              :show-reset-button="false"
              @on-copy="onCopyToken"
            />
          </div>
          <div
            v-if="botSecret && showAccessToken && type === MODAL_TYPES.CREATE"
            class="grid gap-1"
          >
            <p class="type-polaris-body text-polaris-muted-foreground">
              {{ $t('AGENT_BOTS.SECRET.CREATED_DESC') }}
            </p>
            <span class="type-polaris-label">
              {{ $t('AGENT_BOTS.SECRET.LABEL') }}
            </span>
            <AccessToken
              :value="botSecret"
              :show-reset-button="false"
              @on-copy="onCopySecret"
            />
          </div>
        </div>
      </FoldingGroup>
    </form>
  </EditSheet>
</template>
